import { ChronoUnit, Instant, LocalDate } from "@js-joda/core";
import * as Sentry from "@sentry/capacitor";
import jwtDecode from "jwt-decode";
import { CommCenterTicketCreateParams } from "./activities/AppNavigator";
import { HttpRequest } from "./HttpRequest";
import * as messages from "./messages";
import {
  AgencyDocumentCreate,
  AgencyDocumentCreateResponse,
  AgencyId,
  AgencyMemberId,
  AssignedWithOvertime,
  BankAccountNamesResponse,
  BankAccountTypesResponse,
  CaregiverCreateManualTimeSheetRequest,
  CaregiverCreateManualTimeSheetResponse,
  CaregiverCreateTicketParams,
  CaregiverDocumentUploadId,
  CaregiverGetManualTimeSheetResponse,
  CaregiverId,
  CaregiverNotificationId,
  CaregiverPaystubPdfRequest,
  CaregiverSendCommCenterMessageRequest,
  CaregiverSetBankInformation,
  CaregiverViewCommCenterTicketMessages,
  ChatRoomId,
  ConfirmedPending,
  CreateComplianceFormDocumentRecordRequest,
  EditCaregiverUploadDocumentMobileRequest,
  JWToken,
  MobileLiveUpdateResponse,
  PassportCategoryId,
  PassportDocumentCreate,
  PassportDocumentCreateResponse,
  PatientDocumentScheduleId,
  PatientDocumentTypeId,
  PatientTaskCompletionDate,
  PatientTaskInstanceEditScheduleDateTime,
  PlanOfCareId,
  RecruitmentCaregiverApplicationQuestionId,
  SchedulePatientTaskInstanceRequest,
  TrainingCenterBundleDueDateId,
  TrainingCenterBundleItemId,
  TrainingCenterCaregiverVideoBundleItemWatchSegmentParams,
  UploadScannedDocument,
  VisitInstanceDutySheet,
  VisitInstanceId,
  WithdrawRequest,
} from "./messages";
import { SimplifiedAgencies } from "./messages/Agency";
import {
  CaregiverAvailability,
  CaregiverStaffingPreferencesResponse,
  CaregiverUpdatePushNotificationStatusRequest,
  CaregiverUploadDocumentsResponse,
  CaregiverUploadPhotoResponse,
  CaregiverUploadVideoResponse,
  NullableCaregiverAvailability,
  NullablePromoPopover,
  UnscheduleReason,
  UpdateCaregiverStaffingPreferenceRequest,
} from "./messages/Caregiver";
import {
  CoronaReportAgencyMemberChatPerson,
  CoronaReportAllQuestionsResponse,
  CoronaReportSubmitParams,
} from "./messages/Corona";
import { HRAnswerResult, HRQuestion } from "./messages/HR";
import { Icd10Codes } from "./messages/IcdCode";
import {
  PatientMedicationProfileCreateParams,
  PatientMedicationProfileEditParams,
  PatientMedicationProfileItem,
} from "./messages/MedicationProfile";
import {
  CaregiverCertification,
  CaregiverDocumentTypeId,
  CaregiverSurveyQuestionId,
  CommCenterTicketId,
  ImportedVisitId,
  PatientContractDiagnosisCodeId,
  PatientDocumentVersionId,
  PatientId,
  PatientTaskInstanceId,
  VisitBroadcastId,
} from "./messages/ObjectIds";
import { PatientDetailDataRes } from "./messages/Patient";
import {
  CaregiverPatientContractDiagnosisSetParams,
  PatientContractDiagnosisCodes,
  PatientContractDiagnosisSetParams,
} from "./messages/PatientContractDiagnosisCode";
import {
  PatientDocumentAnswers,
  PatientDocumentContentItem,
  PatientDocumentCreateAnswer,
  PatientDocumentScanCreate,
  PatientDocumentsPdfUrl,
  PatientDocumentSubmissionDate,
  PatientDocumentTaskInstanceSubmitRequest,
} from "./messages/PatientDocuments";
import { PlanOfCareResponse, POCSignature } from "./messages/PlanOfCare";
import { CaregiverNotificationSettings } from "./messages/PushNotificationMessages";
import {
  CaregiverRecruitmentData,
  CaregiverRecruitmentQuestionAnswer,
  CaregiverRecruitmentShowStatus,
  RecruitmentUploadDoc,
  RecruitmentUploadDocUrl,
} from "./messages/Recruitment";
import {
  CaregiverVisitEditRequestList,
  ScheduledVisitEditRequestParams,
} from "./messages/ScheduledVisitEditRequest";
import {
  TrainingCenterCaregiverPDFBundleItemWatchSegmentParams,
  TrainingCenterCaregiverQuestionAnswer,
  TrainingCenterCaregiverSubmitTestParams,
  TrainingCenterDisplayLanguage,
} from "./messages/TrainingCenter";
import {
  NursingQuestionVBPAnswer,
  NursingQuestionVBPList,
  NursingQuestionVBPReportSubmit,
  VBPItemsForVisit,
  VBPReportSubmit,
} from "./messages/VBP";
import {
  CaregiverAssignedVisits,
  CaregiverRequestsVisitRequest,
  ClockinClockoutCheckAdjustmentStatusResponse,
  ClockinClockoutRecordSelfieCreateParams,
  ClockInOutCallType,
  FlexibleRequestParamsReq,
  NotInterestedReason,
  ResceduleVisitInstanceDetails,
  RNVisitRequestParams,
  ScheduledVisitLocation,
  SplitShiftShift,
  VisitInstanceClockinClockoutRequestParams,
} from "./messages/Visit";
import { messageValidator } from "./MessageValidation";
import { setLocalStorageItem } from "./storage_utils";
import { FullDescriptionSpeechMark } from "./system/AppState/AppStateTrainingCenter";
import { EventSource } from "./system/EventSource";
import { getDeviceUUID } from "./utils/DeviceUtils";
import { AppVersion } from "./version";

export interface APIConfig {
  readonly apiBaseUrl: string;
  readonly socketIOServerUrl: string;
}

export function apiConfigEqual(a: APIConfig, b: APIConfig): boolean {
  return a.apiBaseUrl === b.apiBaseUrl;
}

const AppVersionHeader = "X-MedFlyt-App-Version";

const DeviceUUIDHeader = "X-MedFlyt-Device-UUID";

const GrantTypeHeader = "X-MedFlyt-grant-type";

const AppPlatformHeader = "Medflyt-App-Platform";

export class AppVersionOutdated {
  protected dummy!: AppVersionOutdated;
}

export class UserLocked {
  protected dummy!: UserLocked;
}

const userLocked = new UserLocked();

const appVersionOutdated = new AppVersionOutdated();

export function isAppVersionOutdated<T>(x: T | AppVersionOutdated): x is AppVersionOutdated {
  return x === appVersionOutdated;
}

export function isUserLocked<T>(x: T | UserLocked): x is UserLocked {
  return x === userLocked;
}

/**
 * @param forType
 * @param val
 */
// TODO maybe best not to export, & create wrapper for Socket.io usage
export function validateApiResponseMessage<T>(forType: string, val: string): T {
  let data: any;
  try {
    data = JSON.parse(val);
  } catch (e) {
    Sentry.captureException(e);
    throw new InvalidResponseError("Invalid JSON", e.message, undefined, undefined);
  }

  const validate = messageValidator(forType);
  if (validate(data) !== true) {
    console.error(validate.errors);
    Sentry.captureException(
      new Error(`Error validating against JSON schema: ${JSON.stringify(validate.errors)}\n${val}`)
    );
    throw new InvalidResponseError(
      "JSON schema validation error",
      "",
      JSON.parse(val),
      validate.errors
    );
  }

  return data;
}

// GET - /mobile/version_check
export async function getNewVersionFromServer(
  apiConfig: APIConfig
): Promise<MobileLiveUpdateResponse> {
  const url = `${apiConfig.apiBaseUrl}/mobile/version_check`;

  const req = new HttpRequest(
    "GET",
    url,
    concatDeviceHeaders([[AppVersionHeader, AppVersion]]),
    null
  );
  const rsp = await req.fetch();
  if (rsp.status >= 200 && rsp.status < 300) {
    try {
      const parsed = validateApiResponseMessage<MobileLiveUpdateResponse>(
        "MobileLiveUpdateResponse",
        rsp.responseText
      );
      return parsed;
    } catch (error) {
      console.error("Parsing error", error);
      return { shouldUpdate: false };
    }
  }
  return { shouldUpdate: false };
}

/**
 * Returns true if the phoneNumber was valid and exists in the server.
 *
 * Otherwise returns false
 */
export async function requestCode(
  apiConfig: APIConfig,
  phoneNumber: string
): Promise<boolean | AppVersionOutdated> {
  const url = `${apiConfig.apiBaseUrl}/auth/caregiver/${phoneNumber}/request_code`;
  const req = new HttpRequest(
    "POST",
    url,
    concatDeviceHeaders([[AppVersionHeader, AppVersion]]),
    null
  );
  const rsp = await req.fetch();

  if (rsp.status === 404) {
    return false;
  } else if (rsp.status >= 200 && rsp.status < 300) {
    // TODO validate that there is no response body
    return true;
  } else if (rsp.status === 422) {
    return appVersionOutdated;
  } else if (rsp.status >= 400 && rsp.status < 500) {
    throw new StatusCodeError(rsp.status, url, rsp.responseText);
  } else {
    throw new ServerError(rsp.status, url);
  }
}

export interface JWTPayload {
  exp: number;
  userLoginDetails: {
    type: "CaregiverLoginResponse";
    caregiver?: messages.CaregiverMobile;
    caregiverId?: CaregiverId;
  };
}

export function getJwtData(jwt: string): JWTPayload {
  return jwtDecode<JWTPayload>(jwt);
}

export interface LoginData {
  tokens: messages.JWTokens;
  payload: JWTPayload;
}

/**
 * @return null if the login code was incorrect
 */
export async function login(
  apiConfig: APIConfig,
  phoneNumber: string,
  params: messages.CaregiverLoginRequest,
  platform: string
): Promise<LoginData | null | AppVersionOutdated | UserLocked> {
  const req = new HttpRequest(
    "POST",
    apiConfig.apiBaseUrl + `/auth/caregiver/${phoneNumber}/login2`,
    concatDeviceHeaders([
      ["Content-Type", "application/json"],
      [AppVersionHeader, AppVersion],
      [AppPlatformHeader, platform],
    ]),
    JSON.stringify(params)
  );

  const rsp = await req.fetch();

  if (rsp.status >= 200 && rsp.status < 300) {
    // TODO validate response against JSON schema
    const response: messages.JWTokens = JSON.parse(rsp.responseText);
    const decoded = getJwtData(response.accessJWT);

    return { tokens: response, payload: decoded };
  } else if (rsp.status === 401) {
    return null;
  } else if (rsp.status === 422) {
    return appVersionOutdated;
  } else if (rsp.status === 423) {
    return userLocked;
  } else {
    throw new Error(rsp.status.toString());
  }
}

export async function getNewAuthToken(
  apiConfig: APIConfig,
  refreshJWT: string
): Promise<LoginData | null | AppVersionOutdated | UserLocked> {
  const req = new HttpRequest(
    "POST",
    apiConfig.apiBaseUrl + `/auth/token`,
    concatDeviceHeaders([
      ["Content-Type", "application/json"],
      [AppVersionHeader, AppVersion],
      [GrantTypeHeader, refreshJWT],
    ]),
    null
  );

  const rsp = await req.fetch();

  if (rsp.status >= 200 && rsp.status < 400) {
    // TODO validate response against JSON schema
    const response: messages.JWTokens = JSON.parse(rsp.responseText);
    const decoded = getJwtData(response.accessJWT);
    return { tokens: response, payload: decoded };
  } else if (rsp.status === 401) {
    return null;
  } else if (rsp.status === 422) {
    return appVersionOutdated;
  } else if (rsp.status === 423) {
    return userLocked;
  } else {
    throw new Error(rsp.status.toString());
  }
}

export async function migrateTokenToJWT(
  apiConfig: APIConfig,
  authToken: string
): Promise<LoginData | null | AppVersionOutdated | UserLocked> {
  const req = new HttpRequest(
    "POST",
    apiConfig.apiBaseUrl + `/auth/caregiver/migrate_token`,
    concatDeviceHeaders([
      ["Content-Type", "application/json"],
      [AppVersionHeader, AppVersion],
      ["Authorization", "Token " + authToken],
    ]),
    null
  );

  const rsp = await req.fetch();

  if (rsp.status >= 200 && rsp.status < 400) {
    // TODO validate response against JSON schema
    const response: messages.JWTokens = JSON.parse(rsp.responseText);
    const decoded = getJwtData(response.accessJWT);
    return { tokens: response, payload: decoded };
  } else if (rsp.status === 401) {
    return null;
  } else if (rsp.status === 422) {
    return appVersionOutdated;
  } else if (rsp.status === 423) {
    return userLocked;
  } else {
    throw new Error(rsp.status.toString());
  }
}

/**
 * @return null if the login code was incorrect
 */
export async function getPassthroughTokenData(
  apiConfig: APIConfig,
  passthroughToken: string
): Promise<LoginData> {
  const req = new HttpRequest(
    "POST",
    apiConfig.apiBaseUrl + `/auth/caregiver/passthrough2`,
    [
      ["Content-Type", "application/json"],
      [AppVersionHeader, AppVersion],
    ],
    JSON.stringify({ passthrough: passthroughToken })
  );

  const rsp = await req.fetch();

  if (rsp.status >= 200 && rsp.status < 400) {
    const response: messages.JWTokens = JSON.parse(rsp.responseText);

    return { tokens: response, payload: getJwtData(response.accessJWT) };
  } else {
    throw new Error(rsp.status.toString());
  }
}

export async function newCaregiver(
  apiConfig: APIConfig,
  accessJWT: string,
  refreshJWT: string,
  params: messages.CaregiverCreateParams,
  platform: string
): Promise<LoginData | AppVersionOutdated> {
  const req = new HttpRequest(
    "POST",
    apiConfig.apiBaseUrl + `/caregiver/new`,
    concatDeviceHeaders([
      ["Content-Type", "application/json"],
      [AppVersionHeader, AppVersion],
      ["Authorization", "Token " + accessJWT],
      [AppPlatformHeader, platform],
    ]),
    JSON.stringify(params)
  );

  const rsp = await req.fetch();

  if (rsp.status >= 200 && rsp.status < 300) {
    const newAuthToken = await getNewAuthToken(apiConfig, refreshJWT);

    if (newAuthToken === null) {
      throw new Error(
        "Unhandled unusual edge case: newAuthToken is null (no caregiver) after we created a new caregiver"
      );
    }

    if (isUserLocked(newAuthToken)) {
      throw new Error(
        "Unhandled unusual edge case: newAuthToken is userLocked after we created a new caregiver"
      );
    }

    return newAuthToken;
  } else if (rsp.status === 422) {
    return appVersionOutdated;
  } else {
    throw new Error("Internal Server Error: " + rsp.status);
  }
}

export async function getAWSPollyMarks(url: string): Promise<FullDescriptionSpeechMark[]> {
  const req = new HttpRequest("GET", url, [], null);

  const rsp = await req.fetch();

  if (rsp.status >= 200 && rsp.status < 300) {
    return rsp.responseText
      .trim()
      .split("\n")
      .map((x) => JSON.parse(x));
  } else {
    throw new Error("Internal Server Error: " + rsp.status);
  }
}

type AuthToken = string;

export type AuthInfo = AuthInfoV1 | AuthInfoV2;

/**
 * @deprecated since 4.0.19
 * @see {AuthInfoV2}
 */
export interface AuthInfoV1 {
  readonly caregiverId: CaregiverId;
  readonly authToken: AuthToken;
}

export interface AuthInfoV2 {
  readonly accessJWT: JWToken;
  readonly refreshJWT: JWToken;
}

/**
 * Signals that the server returned an error
 */
export class ServerError extends Error {
  __proto__: Error; // tslint:disable-line:variable-name
  constructor(statusCode: number, url: string) {
    const trueProto = new.target.prototype;
    super(`Server returned an error: ${statusCode}, ${url}`);

    this.__proto__ = trueProto;

    this.statusCode = statusCode;
    this.url = url;
  }

  readonly statusCode: number;
  readonly url: string;
}

export interface APIAuthInfo {
  readonly accessJWT: JWToken;
  readonly refreshJWT: JWToken;
  readonly caregiverId: CaregiverId;
  readonly exp: number;
}

export class API {
  static ServerError = ServerError;
  static validateApiResponseMessage = validateApiResponseMessage;

  public readonly categoriesUpdated = new EventSource<void>();

  private static jsonHeader: [string, string] = ["Content-Type", "application/json"];
  // private static fileUpload: [string, undefined] = ["Content-Type", undefined];
  // private static multipartBoundary: [string, string] = ["boudary", "xxx"];
  public authExpired = new EventSource<void>();
  public userIsBlocked = new EventSource<void>();
  public appNeedsUpdate = new EventSource<void>();

  public constructor(
    public readonly apiConfig: APIConfig,
    public authInfo: APIAuthInfo,
    public readonly platform: string
  ) {}

  /**
   * Loads state from a previous session, saved with 'save'
   */
  public static Load(apiConfig: APIConfig, savedAuthInfo: AuthInfoV2, platform: string): API {
    const { userLoginDetails, exp } = getJwtData(savedAuthInfo.accessJWT);

    if (userLoginDetails.caregiverId === undefined) {
      throw new Error("Can't load auth info without caregiverId");
    }

    return new API(
      apiConfig,
      {
        accessJWT: savedAuthInfo.accessJWT,
        refreshJWT: savedAuthInfo.refreshJWT,
        caregiverId: userLoginDetails.caregiverId,
        exp: exp,
      },
      platform
    );
  }

  /**
   * Returns a string that contains the serialized auth info. This can
   * then be saved to persistent storage and loaded again later with 'Load'
   */
  public save(): string {
    return JSON.stringify(this.authInfo);
  }

  public logout(): Promise<void> {
    return this.req_noResponseBody("POST", "/auth/caregiver/logout");
  }

  activeRefreshRequest: Promise<void> | null = null;

  public refreshTokenIfNeeded(): Promise<void> {
    const LEFT_MINUTES_FOR_REFRESH = 5;

    const shouldRefresh = Instant.ofEpochSecond(this.authInfo.exp).isBefore(
      Instant.now().plus(LEFT_MINUTES_FOR_REFRESH, ChronoUnit.MINUTES)
    );

    return shouldRefresh ? this.refreshToken() : Promise.resolve();
  }

  public async refreshToken(): Promise<void> {
    if (this.activeRefreshRequest !== null) {
      console.log("[api] waiting for previous refresh to finish");
      return this.activeRefreshRequest;
    }

    this.activeRefreshRequest = new Promise((resolve, reject) => {
      getNewAuthToken(this.apiConfig, this.authInfo.refreshJWT)
        .then((response) => {
          if (response !== null && "payload" in response) {
            if (response.payload.userLoginDetails.caregiver === undefined) {
              return reject(new Error("Can't refresh auth info without caregiverId"));
            }

            this.authInfo = {
              exp: response.payload.exp,
              accessJWT: response.tokens.accessJWT,
              refreshJWT: response.tokens.refreshJWT,
              caregiverId: response.payload.userLoginDetails.caregiver.id,
            };

            const SAVED_AUTH_KEY = "medflyt_app_save_auth";
            setLocalStorageItem(SAVED_AUTH_KEY, JSON.stringify(this.authInfo));
          }

          return resolve();
        })
        .finally(() => (this.activeRefreshRequest = null));
    });

    return this.activeRefreshRequest;
  }

  public setBadLoginAttempt(phoneNumber: string): Promise<void> {
    return this.req_noResponseBody(
      "PUT",
      `/auth/caregiver/${this.authInfo.caregiverId}/${phoneNumber}/invalid_pincode_attempt`
    );
  }

  public registerFcmToken(token: messages.FCMRegistrationToken): Promise<void> {
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/fcm_tokens`,
      token
    );
  }

  public getCaregiverAgencyMembers(): Promise<messages.CaregiverAgencyMembers> {
    return this.req<messages.CaregiverAgencyMembers>(
      "CaregiverAgencyMembers",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/agency_members`
    );
  }

  public getCaregiver(): Promise<messages.CaregiverMobile> {
    return this.req<messages.CaregiverMobile>(
      "CaregiverMobile",
      "GET",
      `/caregivers2/${this.authInfo.caregiverId}`
    );
  }

  public caregiverUploadPhoto(dataUri: string): Promise<void> {
    const body: messages.PhotoUpload = {
      photoData: dataUri,
    };
    return this.req_noResponseBody("PUT", `/caregivers/${this.authInfo.caregiverId}/photo`, body);
  }

  public caregiverUpdateProfile(params: messages.CaregiverUpdateParams): Promise<void> {
    return this.req_noResponseBody("PATCH", `/caregiver/${this.authInfo.caregiverId}`, params);
  }

  public getCaregiverVisits(): Promise<messages.CaregiverVisitBroadcastList> {
    return this.req<messages.CaregiverVisitBroadcastList>(
      "CaregiverVisitBroadcastList",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/visits9`
    );
  }

  // GET /caregivers/:caregiverId/patient_task_instances/:patientTaskInstanceId/patient_schedule
  public getPatientScheduleByTaskInstanceId(
    taskInstanceId: PatientTaskInstanceId
  ): Promise<messages.GetPatientScheduleByTaskInstanceResponse> {
    return this.req<messages.GetPatientScheduleByTaskInstanceResponse>(
      "GetPatientScheduleByTaskInstanceResponse",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/patient_task_instances/${taskInstanceId}/patient_schedule`
    );
  }

  // POST /agencies/:agencyId/agency_members/:agencyMemberId/notes
  public async postPatientNote(
    taskInstanceId: PatientTaskInstanceId,
    noteRichText: string
  ): Promise<void> {
    const payload = {
      noteRichText,
    };

    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/patient_task_instances/${taskInstanceId}/notes`,
      payload
    );
  }

  // GET /caregivers/:caregiverId/patients/:patientId/primary_physician
  public getPatientPrimaryPhysician(
    patientId: PatientId
  ): Promise<messages.PatientPrimaryPhysician> {
    return this.req<messages.PatientPrimaryPhysician>(
      "PatientPrimaryPhysician",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/patients/${patientId}/v2/primary_physician`
    );
  }

  // GET /caregivers/:caregiverId/patients/:patientId/physicians
  public getAllPhysicians(patientId: PatientId): Promise<messages.PatientAvailablePhysicians> {
    return this.req<messages.PatientAvailablePhysicians>(
      "PatientAvailablePhysicians",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/patients/${patientId}/physicians`
    );
  }

  // POST /caregivers/:caregiverId/patients/:patientId/physicians/:physicianId
  public setPatientPhysician(
    patientId: PatientId,
    physicianId: messages.PhysicianId
  ): Promise<void> {
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/patients/${patientId}/physicians/${physicianId}`
    );
  }

  // POST /caregivers/:caregiverId/patients/:patientId/physicians
  public addNewPhysician(patientId: PatientId, params: messages.NewPhysicianParams) {
    return this.req<messages.NewPhysicianCreated>(
      "NewPhysicianCreated",
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/patients/${patientId}/v2/physicians`,
      params
    );
  }

  public getPatientTaskInstances(): Promise<messages.PatientTaskInstancesList> {
    return this.req<messages.PatientTaskInstancesList>(
      "PatientTaskInstancesList",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/patient_tasks`
    );
  }

  public getPatientTaskBroadcasts(): Promise<messages.PatientTaskBroadcastList> {
    return this.req<messages.PatientTaskBroadcastList>(
      "PatientTaskBroadcastList",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/patient_task_broadcasts`
    );
  }

  public setTaskBroadcastAsApproved(patientTaskInstanceId: PatientTaskInstanceId): Promise<void> {
    return this.req_noResponseBody(
      "POST",
      `/caregiver/${this.authInfo.caregiverId}/patient_task_broadcasts/${patientTaskInstanceId}/assign`
    );
  }

  public getCaregiverPatients(): Promise<messages.CaregiverPatientsResponse> {
    return this.req<messages.CaregiverPatientsResponse>(
      "CaregiverPatientsResponse",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/patients`
    );
  }

  public getTrainingCenterBundles(): Promise<messages.CaregiverTrainingCenterBundles> {
    return this.req<messages.CaregiverTrainingCenterBundles>(
      "CaregiverTrainingCenterBundles",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/training_center/all_bundles`
    );
  }

  public getTrainingCenterBundle(
    trainingCenterBundleDueDateId: TrainingCenterBundleDueDateId
  ): Promise<messages.TrainingCenterCaregiverBundle> {
    return this.req<messages.TrainingCenterCaregiverBundle>(
      "TrainingCenterCaregiverBundle",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/training_center/bundles/${trainingCenterBundleDueDateId}`
    );
  }

  public trainingCenterVideoBundleItemWatchSegment(
    trainingCenterBundleDueDateId: TrainingCenterBundleDueDateId,
    trainingCenterBundleItemId: TrainingCenterBundleItemId,
    language: TrainingCenterDisplayLanguage,
    clientUniqueId: number,
    segment: TrainingCenterCaregiverVideoBundleItemWatchSegmentParams
  ): Promise<messages.TrainingCenterCaregiverVideoWatchData> {
    return this.req<messages.TrainingCenterCaregiverVideoWatchData>(
      "TrainingCenterCaregiverVideoWatchData",
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/training_center/bundles/${trainingCenterBundleDueDateId}/items/${trainingCenterBundleItemId}/video/${language}/${clientUniqueId}/watch_segment`,
      segment
    );
  }

  public trainingCenterPDFBundleItemWatchSegment(
    trainingCenterBundleDueDateId: TrainingCenterBundleDueDateId,
    trainingCenterBundleItemId: TrainingCenterBundleItemId,
    clientUniqueId: number,
    segment: TrainingCenterCaregiverPDFBundleItemWatchSegmentParams
  ): Promise<messages.TrainingCenterCaregiverPDFWatchData> {
    return this.req<messages.TrainingCenterCaregiverPDFWatchData>(
      "TrainingCenterCaregiverPDFWatchData",
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/training_center/bundles/${trainingCenterBundleDueDateId}/items/${trainingCenterBundleItemId}/pdf/${clientUniqueId}/watch_segment`,
      segment
    );
  }

  public trainingCenterCaregiverSaveDraftTestAnswer(
    trainingCenterBundleDueDateId: TrainingCenterBundleDueDateId,
    trainingCenterBundleItemId: TrainingCenterBundleItemId,
    params: TrainingCenterCaregiverQuestionAnswer
  ): Promise<void> {
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/training_center/bundles/${trainingCenterBundleDueDateId}/items/${trainingCenterBundleItemId}/submit_draft_question_answer`,
      params
    );
  }

  public trainingCenterBundleItemSubmitTest(
    trainingCenterBundleDueDateId: TrainingCenterBundleDueDateId,
    trainingCenterBundleItemId: TrainingCenterBundleItemId,
    params: TrainingCenterCaregiverSubmitTestParams
  ): Promise<messages.TrainingCenterCaregiverSubmitTestResult> {
    return this.req<messages.TrainingCenterCaregiverSubmitTestResult>(
      "TrainingCenterCaregiverSubmitTestResult",
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/training_center/bundles/${trainingCenterBundleDueDateId}/items/${trainingCenterBundleItemId}/submit_test`,
      params
    );
  }

  public getTrainingCenterBundleCertificatePdf(
    trainingCenterBundleDueDateId: TrainingCenterBundleDueDateId
  ): Promise<messages.TrainingCenterCaregiverCertificatePdf> {
    return this.req<messages.TrainingCenterCaregiverCertificatePdf>(
      "TrainingCenterCaregiverCertificatePdf",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/training_center/bundles/${trainingCenterBundleDueDateId}/certificate_pdf`
    );
  }

  public getTrainingCenterBundleAndCaregiverOffices(
    trainingCenterBundleDueDateId: TrainingCenterBundleDueDateId
  ): Promise<messages.BundleOfficeInfo> {
    return this.req<messages.BundleOfficeInfo>(
      "BundleOfficeInfo",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/training_center/bundles/${trainingCenterBundleDueDateId}/offices`
    );
  }

  public setVisitPreviewed(params: messages.VisitsPreviewedRequest): Promise<void> {
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/visits/previewed`,
      params
    );
  }

  public setTaskBroadcastMoreDetailsViewed(taskBroadcastsId: PatientTaskInstanceId): Promise<void> {
    return this.req_noResponseBody(
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/patient_task_broadcasts/${taskBroadcastsId}/more_details_viewed`
    );
  }

  public setVisitMoreDetailsViewed(visitBroadcastId: VisitBroadcastId): Promise<void> {
    return this.req_noResponseBody(
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/visits/${visitBroadcastId}/more_details_viewed`
    );
  }

  /**
   * @returns null on success. string if there is an error (contains error message)
   */
  public async setRNVisitAccepted(
    visitId: VisitBroadcastId,
    requestParams: RNVisitRequestParams
  ): Promise<string | null> {
    try {
      await this.req_noResponseBody(
        "PUT",
        `/caregivers/${this.authInfo.caregiverId}/visits/${visitId}/accepted_with_requested_times`,
        requestParams
      );
      return null;
    } catch (err) {
      if (err instanceof StatusCodeError) {
        if (err.statusCode === 409) {
          return err.body;
        }
      }
      throw err;
    }
  }

  // PUT /caregivers/:caregiverId/visit_broadcasts/:visitBroadcastId/accepted_flexible
  public setFlexibleVisitAccepted(
    visitBroadcastId: VisitBroadcastId,
    visitRequest: FlexibleRequestParamsReq
  ): Promise<void> {
    return this.req_noResponseBody(
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/visit_broadcasts/${visitBroadcastId}/accepted_flexible`,
      visitRequest
    );
  }

  public setVisitAccepted(
    visitBroadcastId: VisitBroadcastId,
    visitRequest: CaregiverRequestsVisitRequest
  ): Promise<void> {
    return this.req_noResponseBody(
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/visits/${visitBroadcastId}/accepted2`,
      visitRequest
    );
  }

  public async cancelAssignedVisit(visitBroadcastId: VisitBroadcastId): Promise<boolean> {
    try {
      await this.req_noResponseBody(
        "DELETE",
        `/caregivers/${this.authInfo.caregiverId}/visits/${visitBroadcastId}/accepted`
      );

      return true;
    } catch (err) {
      if (err instanceof ServerError) {
        if (err.statusCode === 400) {
          return false;
        }
      }

      throw err;
    }
  }

  public setCaregiverAvailability(availability: CaregiverAvailability): Promise<void> {
    return this.req_noResponseBody(
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/availability`,
      availability
    );
  }

  public getCaregiverAvailability(): Promise<NullableCaregiverAvailability> {
    return this.req<NullableCaregiverAvailability>(
      "NullableCaregiverAvailability",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/availability`
    );
  }

  public getRescheduleVisitInstanceDetails(
    visitInstanceId: VisitInstanceId
  ): Promise<ResceduleVisitInstanceDetails> {
    return this.req<ResceduleVisitInstanceDetails>(
      "ResceduleVisitInstanceDetails",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/visit_instances/${visitInstanceId}/reschedule_details`
    );
  }

  public rescheduleVisitInstance(visitInstanceId: VisitInstanceId, params: RNVisitRequestParams) {
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/visit_instances/${visitInstanceId}/reschedule_visit`,
      params
    );
  }

  public async cancelVisitRequest(
    visitBroadcastId: VisitBroadcastId,
    visitInstances: VisitInstanceId[]
  ): Promise<boolean> {
    let toReturn;
    try {
      await this.req_noResponseBody(
        "DELETE",
        `/caregivers/${
          this.authInfo.caregiverId
        }/visits/${visitBroadcastId}/requested2?visitInstances=${visitInstances.join(",")}`
      );
      toReturn = true;
    } catch (err) {
      if (err instanceof ServerError) {
        if (err.statusCode === 400) {
          return false;
        }
      }

      throw err;
    }

    return toReturn;
  }

  public getNotifications(): Promise<messages.PushNotificationInfoList> {
    return this.req<messages.PushNotificationInfoList>(
      "PushNotificationInfoList",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/notifications`
    );
  }

  public getNotificationSettings(): Promise<CaregiverNotificationSettings> {
    return this.req<CaregiverNotificationSettings>(
      "CaregiverNotificationSettings",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/notification_settings`
    );
  }

  public setNotificationSettings(
    notificationsSetting: CaregiverNotificationSettings
  ): Promise<void> {
    return this.req_noResponseBody(
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/notification_settings`,
      notificationsSetting
    );
  }

  public setNotificationsViewed(): Promise<void> {
    return this.req_noResponseBody(
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/notifications_viewed`
    );
  }

  public setNotificationClicked(notificationId: CaregiverNotificationId): Promise<void> {
    return this.req_noResponseBody(
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/notifications/${notificationId}/click`
    );
  }

  public async getAllChatRooms(): Promise<messages.ChatRoomList> {
    return this.req<messages.ChatRoomList>(
      "ChatRoomList",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/chat_rooms`
    );
  }

  public async getChatRoom(chatRoomId: ChatRoomId): Promise<messages.ChatRoom> {
    return this.req<messages.ChatRoom>("ChatRoom", "GET", `/chats/${chatRoomId}`);
  }

  public async getAgencyMemberChatRoom(agencyMemberId: AgencyMemberId) {
    return this.req<messages.ChatRoom>(
      "ChatRoom",
      "GET",
      `/chats/agency_member_caregiver_chat_rooms/${agencyMemberId}/${this.authInfo.caregiverId}`
    );
  }

  public async getVisitChatRoom(visitId: VisitBroadcastId): Promise<messages.ChatRoom> {
    return this.req<messages.ChatRoom>(
      "ChatRoom",
      "GET",
      `/chats/caregiver_visit_chat_rooms/${visitId}/${this.authInfo.caregiverId}`
    );
  }

  public async getPatientChatRoom(patientId: PatientId): Promise<messages.ChatRoom> {
    return this.req<messages.ChatRoom>(
      "ChatRoom",
      "GET",
      `/chats/caregiver/${this.authInfo.caregiverId}/patient/${patientId}`
    );
  }

  public async getRefCode(): Promise<string> {
    const resp: any = await this.req<messages.Any>("Any", "GET", `/user/ref_code`);
    return resp.refCode;
  }

  public async getCaregiverScore(): Promise<messages.CaregiverScore> {
    return this.req<messages.CaregiverScore>(
      "CaregiverScore",
      "GET",
      `/caregiver/${this.authInfo.caregiverId}/biweekly/score`
    );
  }

  public async getTopCaregiverScores(): Promise<messages.CaregiverScores> {
    return this.req<messages.CaregiverScores>(
      "CaregiverScores",
      "GET",
      `/caregivers/top/biweekly/scores`
    );
  }

  public async setReferredBy(code: string) {
    return this.req_noResponseBody(
      "PUT",
      `/caregiver/${this.authInfo.caregiverId}/referred_by/${code}`
    );
  }

  public async getReferredBy(): Promise<any> {
    return this.req<any>("Any", "GET", `/caregiver/${this.authInfo.caregiverId}/referred_by`);
  }

  public async setCaregiverClockIn(
    scheduledVisitId: VisitInstanceId,
    caregiverLocation: ScheduledVisitLocation
  ): Promise<boolean> {
    try {
      await this.req_noResponseBody(
        "PUT",
        `/caregivers/${this.authInfo.caregiverId}/visits/${scheduledVisitId}/check_in`,
        caregiverLocation
      );
      return true;
    } catch (err) {
      if (err instanceof ServerError) {
        if (err.statusCode === 409) {
          return false;
        }
      }
      throw err;
    }
  }

  public async postPatientDocAnswer(
    visitInstanceId: VisitInstanceId,
    documentTypeId: PatientDocumentTypeId,
    answer: PatientDocumentCreateAnswer
  ): Promise<void> {
    const url =
      "/caregivers/:caregiverId/visit_instances/:visitInstanceId/patient_documents/:documentTypeId/answer"
        .replace(":caregiverId", this.authInfo.caregiverId.toString())
        .replace(":visitInstanceId", visitInstanceId.toString())
        .replace(":documentTypeId", documentTypeId.toString());

    return this.req_noResponseBody("POST", url, answer);
  }

  public async getPatientDocumentsHistory(
    patientId: PatientId
  ): Promise<messages.CaregiverPatientDocumentResponse> {
    const url = "/caregivers/:caregiverId/patients/:patientId/details"
      .replace(":caregiverId", this.authInfo.caregiverId.toString())
      .replace(":patientId", patientId.toString());

    return this.req<messages.CaregiverPatientDocumentResponse>(
      "CaregiverPatientDocumentResponse",
      "GET",
      url
    );
  }

  public async getLastAnswers(
    visitInstanceId: VisitInstanceId,
    versionId: messages.PatientDocumentVersionId
  ): Promise<PatientDocumentAnswers> {
    const url =
      "/caregivers/:caregiverId/visit_instances/:visitInstanceId/patient_document_versions/:patientDocumentVersionId"
        .replace(":caregiverId", this.authInfo.caregiverId.toString())
        .replace(":visitInstanceId", visitInstanceId.toString())
        .replace(":patientDocumentVersionId", versionId.toString());

    return this.req<PatientDocumentAnswers>("PatientDocumentAnswers", "POST", url);
  }

  public async postSubmitPatientDocument(
    visitInstanceId: VisitInstanceId,
    documentTypeId: PatientDocumentTypeId,
    req: PatientDocumentSubmissionDate
  ): Promise<void> {
    const url =
      "/caregivers/:caregiverId/visit_instances/:visitInstanceId/patient_documents/:documentTypeId"
        .replace(":caregiverId", this.authInfo.caregiverId.toString())
        .replace(":visitInstanceId", visitInstanceId.toString())
        .replace(":documentTypeId", documentTypeId.toString());

    return this.req_noResponseBody("POST", url, req);
  }

  public async getPlanOfCare(visitInstanceId: VisitInstanceId): Promise<PlanOfCareResponse> {
    return this.req<PlanOfCareResponse>(
      "PlanOfCareResponse",
      "GET",
      `/caregiver/${this.authInfo.caregiverId}/visit/${visitInstanceId}/plan_of_care`
    );
  }

  public async setPlanOfCareSignature(
    planOfCareId: PlanOfCareId,
    scheduledVisitId: VisitInstanceId,
    signature: POCSignature
  ): Promise<void> {
    return this.req_noResponseBody(
      "PUT",
      `/caregiver/${this.authInfo.caregiverId}/scheduled_visit/${scheduledVisitId}/plan_of_care/${planOfCareId}/signature`,
      signature
    );
  }

  public async setPlanOfCareItemAnswer(
    scheduledVisitId: VisitInstanceId,
    planOfCareId: PlanOfCareId,
    answer: PatientDocumentContentItem[]
  ): Promise<void> {
    return this.req_noResponseBody(
      "PUT",
      `/caregiver/${this.authInfo.caregiverId}/scheduled_visit/${scheduledVisitId}/plan_of_care/${planOfCareId}/version`,
      {
        content: answer,
        isPublished: false,
      }
    );
  }

  public async submitPlanOfCare(
    scheduledVisitId: VisitInstanceId,
    planOfCareId: PlanOfCareId,
    answer: PatientDocumentContentItem[]
  ): Promise<void> {
    return this.req_noResponseBody(
      "PUT",
      `/caregiver/${this.authInfo.caregiverId}/scheduled_visit/${scheduledVisitId}/plan_of_care/${planOfCareId}/version`,
      {
        content: answer,
        isPublished: true,
      }
    );
  }

  // /caregivers/:caregiverId/visit_instances/:visitInstanceId/can_adjust_times
  public getCaregiverCanAdjustVisitInstanceTimes = async (
    visitInstanceId: VisitInstanceId
  ): Promise<ClockinClockoutCheckAdjustmentStatusResponse> => {
    return this.req(
      "ClockinClockoutCheckAdjustmentStatusResponse",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/visit_instances/${visitInstanceId}/can_adjust_times`
    );
  };

  // POST /caregivers/:caregiverId/visit_instances/:visitInstanceId/missed
  public caregiverMarkVisitAsMissed = async (visitInstanceId: VisitInstanceId) => {
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/visit_instances/${visitInstanceId}/missed`
    );
  };

  public setCaregiverVisitInstanceClockInClockOut = async (
    visitInstanceId: VisitInstanceId,
    clockInOutCallType: ClockInOutCallType,
    visitInstanceClockinClockoutRequestParams: VisitInstanceClockinClockoutRequestParams
  ): Promise<boolean> => {
    try {
      await this.req_noResponseBody(
        "POST",
        `/caregivers/${this.authInfo.caregiverId}/visits/${visitInstanceId}/clockin_clockout_record/${clockInOutCallType}`,
        visitInstanceClockinClockoutRequestParams
      );
      return true;
    } catch (err) {
      if (err instanceof ServerError) {
        if (err.statusCode === 409) {
          return false;
        }
      }
      throw err;
    }
  };

  public async getClockedInStatus(): Promise<messages.ClockedInStatus> {
    return this.req<messages.ClockedInStatus>(
      "ClockedInStatus",
      "GET",
      `/caregiver/${this.authInfo.caregiverId}/visit_instance_clocked_in_status`
    );
  }

  public async getAgencyFromCode(agencyCode: string): Promise<SimplifiedAgencies> {
    return this.req<messages.SimplifiedAgencies>(
      "SimplifiedAgencies",
      "GET",
      `/agency/simplified/${agencyCode}`
    );
  }

  public async createRescheduleRequest(
    scheduledVisitId: VisitInstanceId,
    scheduledVisitEditRequestParams: ScheduledVisitEditRequestParams
  ): Promise<boolean> {
    try {
      await this.req_noResponseBody(
        "PUT",
        `/caregiver/${this.authInfo.caregiverId}/scheduled_visit/${scheduledVisitId}/edit_request`,
        scheduledVisitEditRequestParams
      );
      return true;
    } catch (err) {
      if (err instanceof ServerError) {
        if (err.statusCode === 409) {
          return false;
        }
      }
      throw err;
    }
  }

  public async createRescheduleRequestForSingleVisit(
    visitId: VisitBroadcastId,
    scheduledVisitEditRequestParams: ScheduledVisitEditRequestParams
  ) {
    return this.req_noResponseBody(
      "PUT",
      `/caregiver/${this.authInfo.caregiverId}/single_visit/${visitId}/edit_request`,
      scheduledVisitEditRequestParams
    );
  }

  public async getScheduledVisitEditRequests() {
    return this.req<CaregiverVisitEditRequestList>(
      "CaregiverVisitEditRequestList",
      "GET",
      `/caregiver/${this.authInfo.caregiverId}/visit_instance_edit_requests`
    );
  }

  public async getCoronaReport() {
    return this.req<CoronaReportAllQuestionsResponse>(
      "CoronaReportAllQuestionsResponse",
      "GET",
      `/caregiver/${this.authInfo.caregiverId}/all_corona_reports`
    );
  }

  public async getCoronaReportChatPerson(agencyId: AgencyId) {
    return this.req<CoronaReportAgencyMemberChatPerson>(
      "CoronaReportAgencyMemberChatPerson",
      "GET",
      `/caregiver/${this.authInfo.caregiverId}/agency/${agencyId}/chat_person`
    );
  }

  public async getVBPQuestionsForVisit(visitInstanceId: VisitInstanceId) {
    return this.req<VBPItemsForVisit>(
      "VBPItemsForVisit",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/visit_instances/` + visitInstanceId + "/vbp_items"
    );
  }

  public async getVBPNursingQuestionsForVisit(visitInstanceId: VisitInstanceId) {
    return this.req<NursingQuestionVBPList>(
      "NursingQuestionVBPList",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/visit_instances/${visitInstanceId}/vbp_nursing_questions`
    );
  }

  public async submitVBPQuestionsForVisit(
    visitInstanceId: VisitInstanceId,
    vbpReportSubmit: VBPReportSubmit
  ) {
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/visit_instances/${visitInstanceId}/vbp_items`,
      vbpReportSubmit
    );
  }

  /* old vbp answers submission */
  public async submitVBPNursingQuestionsForVisit(
    visitInstanceId: VisitInstanceId,
    nursingQuestionVBPReportSubmit: NursingQuestionVBPReportSubmit
  ) {
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/visit_instances/${visitInstanceId}/vbp_nursing_questions`,
      nursingQuestionVBPReportSubmit
    );
  }

  public async submitVBPNursingQuestionAnswerForVisit(
    visitInstanceId: VisitInstanceId,
    nursingQuestionVBPAnswer: NursingQuestionVBPAnswer
  ) {
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/visit_instances/${visitInstanceId}/vbp_nursing_question_answer`,
      nursingQuestionVBPAnswer
    );
  }

  public async submitCoronaReport(
    visitInstanceId: VisitInstanceId | null,
    answers: CoronaReportSubmitParams
  ) {
    const answersPrep = { ...answers };

    if (visitInstanceId !== null) {
      answersPrep.patientName = null;
      answersPrep.patientAgencyId = null;
    }

    return this.req_noResponseBody(
      "POST",
      `/caregiver/${this.authInfo.caregiverId}/corona_reports_submit?visitInstanceId=${visitInstanceId}`,
      answersPrep
    );
  }

  public async setAgencyRequested(agencyId: AgencyId): Promise<void> {
    return this.req_noResponseBody(
      "POST",
      `/caregiver/${this.authInfo.caregiverId}/join/agency/${agencyId}`
    );
  }

  public async cancelAgencyRequested(agencyId: AgencyId): Promise<void> {
    return this.req_noResponseBody(
      "DELETE",
      `/caregiver/${this.authInfo.caregiverId}/join/agency/${agencyId}`
    );
  }

  public async checkEmploymentStatus(): Promise<CaregiverRecruitmentShowStatus> {
    return this.req<CaregiverRecruitmentShowStatus>(
      "CaregiverRecruitmentShowStatus",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/recruitment/show_status`
    );
  }

  public async getRecruitmentFormInfo(): Promise<CaregiverRecruitmentData> {
    return this.req<CaregiverRecruitmentData>(
      "CaregiverRecruitmentData",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/recruitment/data`
    );
  }

  public uploadPhotos(
    caregiverId: CaregiverId,
    photos: Blob[]
  ): Promise<CaregiverUploadPhotoResponse> {
    const formData = new FormData();

    for (const photo of photos) {
      formData.append("photos", photo);
    }

    return this.req_formData<CaregiverUploadPhotoResponse>({
      method: "POST",
      forType: "CaregiverUploadPhotoResponse",
      endpoint: `/caregivers/${caregiverId}/upload_photos`,
      body: formData,
    });
  }

  public uploadVideos(
    caregiverId: CaregiverId,
    videos: Blob[]
  ): Promise<CaregiverUploadVideoResponse> {
    const formData = new FormData();

    for (const video of videos) {
      formData.append("videos", video);
    }

    return this.req_formData<CaregiverUploadVideoResponse>({
      method: "POST",
      forType: "CaregiverUploadVideoResponse",
      endpoint: `/caregivers/${caregiverId}/upload_videos`,
      body: formData,
    });
  }

  public uploadDocuments(
    caregiverId: CaregiverId,
    documents: Blob[]
  ): Promise<CaregiverUploadDocumentsResponse> {
    const formData = new FormData();

    for (const document of documents) {
      formData.append("files", document);
    }

    return this.req_formData<CaregiverUploadDocumentsResponse>({
      method: "POST",
      forType: "CaregiverUploadDocumentsResponse",
      endpoint: `/caregivers/${caregiverId}/upload_documents`,
      body: formData,
    });
  }

  public chatSend(
    chatRoomId: ChatRoomId,
    requestId: string,
    message: { value: string; images?: string[] }
  ): Promise<void> {
    const body: messages.PostChatMessage = {
      requestId: requestId,
      payload: {
        type: "Message",
        message: message.value,
        images: message.images,
      },
    };
    return this.req_noResponseBody("POST", `/chats/${chatRoomId}`, body);
  }

  public chatLastViewed(chatRoomId: ChatRoomId, messageTime: Instant): Promise<void> {
    const body: messages.LastViewedChatMessage = {
      messageTime: messageTime,
    };
    return this.req_noResponseBody("PUT", `/chats/${chatRoomId}/viewed`, body);
  }

  public async putNotInterested(
    visitBroadcastId: VisitBroadcastId,
    notInterestedReason: NotInterestedReason
  ): Promise<void> {
    return this.req_noResponseBody(
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/visits/${visitBroadcastId}/not_interested`,
      notInterestedReason
    );
  }

  public async assignContactPatientRN(visitId: VisitBroadcastId): Promise<void> {
    return this.req_noResponseBody(
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/visit/${visitId}/assign_contact`
    );
  }

  public async callPatient(visitId: VisitBroadcastId): Promise<void> {
    return this.req_noResponseBody(
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/visit/${visitId}/call_patient`
    );
  }

  public getCaregiverSchedule() {
    return this.req<CaregiverAssignedVisits>(
      "CaregiverAssignedVisits",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/schedule_instances`
    );
  }

  public getPatient(patientId: PatientId): Promise<PatientDetailDataRes> {
    const url = "/caregivers/:caregiverId/patients/:patientId"
      .replace(":caregiverId", this.authInfo.caregiverId.toString())
      .replace(":patientId", patientId.toString());

    return this.req<PatientDetailDataRes>("PatientDetailDataRes", "GET", url);
  }

  public getPromotionalPopover(): Promise<NullablePromoPopover> {
    return this.req<NullablePromoPopover>(
      "NullablePromoPopover",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/promo`
    );
  }

  public postRecruitmentAnswer(
    id: RecruitmentCaregiverApplicationQuestionId,
    answer: string | number | number[] | null
  ) {
    const body: CaregiverRecruitmentQuestionAnswer = {
      answer: answer,
    };
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/recruitment/answer/${id}`,
      body
    );
  }

  public uploadRecruitmentFile(id: RecruitmentCaregiverApplicationQuestionId, data: string) {
    const body: RecruitmentUploadDoc = {
      base64: data,
    };
    return this.req<RecruitmentUploadDocUrl>(
      "RecruitmentUploadDocUrl",
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/recruitment/upload/${id}`,
      body
    );
  }

  public submitRecruitmentForm() {
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/recruitment/submit`
    );
  }

  public async recordCallToAgencyClickByVisitId(
    visitId: VisitBroadcastId,
    agencyId: AgencyId
  ): Promise<void> {
    const url = "/caregiver/:caregiverId/call/agency/:agencyId/visits/:visitId"
      .replace(":caregiverId", this.authInfo.caregiverId.toString())
      .replace(":agencyId", agencyId.toString())
      .replace(":visitInstanceId", visitId.toString());

    return this.req_noResponseBody("POST", url);
  }

  public async recordCallToAgencyClickByVisitInstanceId(
    visitInstanceId: VisitInstanceId,
    agencyId: AgencyId
  ): Promise<void> {
    const url = "/caregiver/:caregiverId/call/agency/:agencyId/visit_instances/:visitInstanceId"
      .replace(":caregiverId", this.authInfo.caregiverId.toString())
      .replace(":agencyId", agencyId.toString())
      .replace(":visitInstanceId", visitInstanceId.toString());

    return this.req_noResponseBody("POST", url);
  }

  public async requestVisitSplitShifts(
    visitId: VisitBroadcastId,
    shifts: SplitShiftShift[]
  ): Promise<void> {
    return this.req_noResponseBody(
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/visits/${visitId}/accepted_split_shift`,
      {
        splitShifts: shifts,
      }
    );
  }

  private authHeader(): [string, string] {
    return ["Authorization", "Token " + this.authInfo.accessJWT];
  }

  public getMedflytPassportCategories() {
    return this.req<messages.PassportDocumentCategories>(
      "PassportDocumentCategories",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/passport_document_categories`
    );
  }

  public getMedflytPassportDocumentTypes(categoryId: PassportCategoryId) {
    return this.req<messages.PassportDocumentTypes>(
      "PassportDocumentTypes",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/passport_document_types/${categoryId}`
    );
  }

  public getMedflytPassportDocuments(documentTypeId: CaregiverDocumentTypeId) {
    return this.req<messages.PassportDocuments>(
      "PassportDocuments",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/passport_documents/${documentTypeId}`
    );
  }

  public getMedflytAgencyDocuments(agencyId: AgencyId, documentTypeId: CaregiverDocumentTypeId) {
    return this.req<messages.AgencyDocuments>(
      "AgencyDocuments",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/agencies/${agencyId}/document_uploads/${documentTypeId}`
    );
  }

  public removeMedflytPassportDocument(documentId: CaregiverDocumentUploadId) {
    return this.req_noResponseBody(
      "DELETE",
      `/caregivers/${this.authInfo.caregiverId}/passport_documents/${documentId}`
    );
  }

  public setMedflytPassportDocumentExpiryDate(
    documentId: CaregiverDocumentUploadId,
    expiryDate: LocalDate
  ) {
    return this.req_noResponseBody(
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/passport_documents/${documentId}`,
      { expiryDate: expiryDate }
    );
  }

  public uploadMedflytPassportDocument(data: PassportDocumentCreate) {
    return this.req<PassportDocumentCreateResponse>(
      "PassportDocumentCreateResponse",
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/passport_documents`,
      data
    );
  }

  public uploadMedflytPassportAgencyDocument(
    agencyId: AgencyId,
    documentTypeId: CaregiverDocumentTypeId,
    data: AgencyDocumentCreate
  ) {
    return this.req<AgencyDocumentCreateResponse>(
      "PassportDocumentCreateResponse",
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/agencies/${agencyId}/document_uploads/${documentTypeId}`,
      data
    );
  }

  public uploadScannedScheduledVisitDocument(
    data: PatientDocumentScanCreate,
    visitInstanceId: VisitInstanceId
  ) {
    const url =
      "/caregivers/:caregiverId/visit_instances/:visitInstanceId/patient_document_scheduled_visits"
        .replace(":caregiverId", this.authInfo.caregiverId.toString())
        .replace(":visitInstanceId", visitInstanceId.toString());

    return this.req<messages.CaregiverPatientDocumentResponse>(
      "CaregiverPatientDocumentResponse",
      "POST",
      url,
      data
    );
  }

  public deleteScannedScheduledVisitDocument(
    caregiverId: CaregiverId,
    visitInstanceId: VisitInstanceId,
    patientDocumentScheduledId: PatientDocumentScheduleId
  ) {
    const url =
      "/caregivers/:caregiverId/visit_instances/:visitInstanceId/patient_document_scheduled_visits/:patientDocumentScheduledId"
        .replace(":caregiverId", caregiverId.toString())
        .replace(":visitInstanceId", visitInstanceId.toString())
        .replace(":patientDocumentScheduledId", patientDocumentScheduledId.toString());

    return this.req_noResponseBody("DELETE", url);
  }

  public getIcdCodeAutoCompleteOptions(searchText: String): Promise<Icd10Codes> {
    return this.req<Icd10Codes>(
      "Icd10Codes",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/icd_10_codes/auto_complete?search_string=${searchText}`
    );
  }

  public getVisitInstanceDocuments(visitInstanceId: VisitInstanceId) {
    const url = "/caregivers/:caregiverId/visit_instances/:visitInstanceId/patient_documents"
      .replace(":caregiverId", this.authInfo.caregiverId.toString())
      .replace(":visitInstanceId", visitInstanceId.toString());

    return this.req<messages.CaregiverPatientDocumentResponse>(
      "CaregiverPatientDocumentResponse",
      "GET",
      url
    );
  }

  public getHRDocsByType(documentTypeId: number) {
    return this.req<messages.CaregiverUploadedDocuments>(
      "CaregiverUploadedDocuments",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/uploaded_documents/hr/${documentTypeId}`
    );
  }

  public getPatientDocTypes(visitId: VisitBroadcastId) {
    return this.req<messages.UploadDocumentCategories>(
      "UploadDocumentCategories",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/visit/${visitId}/uploaded_documents`
    );
  }

  public getPatientDocsByType(visitId: VisitBroadcastId, documentTypeId: number) {
    return this.req<messages.CaregiverUploadedDocuments>(
      "CaregiverUploadedDocuments",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/visit/${visitId}/uploaded_documents/${documentTypeId}`
    );
  }

  public uploadHRFile(imageData: string, documentTypeId: number) {
    const body: UploadScannedDocument = {
      base64: imageData,
    };
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/upload_document/hr/${documentTypeId}`,
      body
    );
  }

  public uploadPatientFile(imageData: string, visitId: VisitBroadcastId, documentTypeId: number) {
    const body: UploadScannedDocument = {
      base64: imageData,
    };
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/visit/${visitId}/upload_document/${documentTypeId}`,
      body
    );
  }

  public searchMedications(value: string) {
    return this.req<messages.MedicationProfileSearchResponse>(
      "MedicationProfileSearchResponse",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/medication_profile/search?value=${value}`
    );
  }

  public searchMedicationsByPatientId(value: string, patientId: PatientId) {
    return this.req<messages.MedicationProfileSearchResponse>(
      "MedicationProfileSearchResponse",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/patient/${patientId}/medication_profile/search?value=${value}`
    );
  }

  public async postPatientMedicationProfileItem(
    visitInstanceId: VisitInstanceId,
    patientMedication: PatientMedicationProfileCreateParams
  ): Promise<PatientMedicationProfileItem> {
    return this.req<PatientMedicationProfileItem>(
      "PatientMedicationProfileItem",
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/visit_instances/${visitInstanceId}/medication_profile`,
      patientMedication
    );
  }

  public editPatientMedicationProfileItem(
    visitInstanceId: VisitInstanceId,
    patientMedication: PatientMedicationProfileEditParams
  ) {
    return this.req_noResponseBody(
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/visit_instances/${visitInstanceId}/medication_profile`,
      patientMedication
    );
  }

  public convertImportedVisit(
    importVisitId: ImportedVisitId,
    patientPhoneNumber: string,
    patientAddress: string | null
  ) {
    const body = {
      importVisitId: importVisitId,
      patientPhoneNumber: patientPhoneNumber,
      patientAddress: patientAddress,
    };
    return this.req<messages.ConvertImportedVisit>(
      "ConvertImportedVisit",
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/convert_import_visit`,
      body
    );
  }

  // GET /caregivers/:caregiverId/visit_instances/:visitInstanceId/patient_diagnosis_codes
  public getPatientDiagnosisCodesByVisitInstanceId(visitInstanceId: VisitInstanceId) {
    return this.req<messages.PatientContractDiagnosisCodes>(
      "PatientContractDiagnosisCodes",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/visit_instances/${visitInstanceId}/patient_diagnosis_codes`
    );
  }

  // GET /caregivers/:caregiverId/patient/:patientId/medication_profile
  public async getPatientMedicationProfile(patientId: PatientId) {
    return this.req<messages.PatientMedicationProfile>(
      "PatientMedicationProfile",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/patient/${patientId}/medication_profile`
    );
  }

  // POST /caregivers/:caregiverId/visit_instances/:visitInstanceId/patient_contract_diagnosis_codes
  public createPatientDiagnosisCodeByVisitInstanceId(
    visitInstanceId: VisitInstanceId,
    params: PatientContractDiagnosisSetParams
  ) {
    return this.req<messages.PatientContractDiagnosisCodes>(
      "PatientContractDiagnosisCodes",
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/visit_instances/${visitInstanceId}/patient_contract_diagnosis_codes`,
      params
    );
  }

  // PUT /caregivers/:caregiverId/patient_contract_diagnosis_codes/:patientContractDiagnosisCode
  public updatePatientDiagnosisCode(
    id: PatientContractDiagnosisCodeId,
    params: PatientContractDiagnosisSetParams
  ) {
    return this.req<messages.PatientContractDiagnosisCodes>(
      "PatientContractDiagnosisCodes",
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/patient_contract_diagnosis_codes/${id}`,
      params
    );
  }

  // DELETE /caregivers/:caregiverId/patient_contract_diagnosis_codes/:patientContractDiagnosisCode
  public deletePatientDiagnosisCode(id: PatientContractDiagnosisCodeId) {
    return this.req_noResponseBody(
      "DELETE",
      `/caregivers/${this.authInfo.caregiverId}/patient_contract_diagnosis_codes/${id}`
    );
  }

  public async getCaregiverSurveyQuestions(): Promise<messages.CaregiverSurveyQuestionsList> {
    return this.req<messages.CaregiverSurveyQuestionsList>(
      "CaregiverSurveyQuestionsList",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/survey_questions`
    );
  }

  public async submitCaregiverSurveyAnswer(
    caregiverSurveyQuestionId: CaregiverSurveyQuestionId,
    answer: messages.CaregiverSurveyQuestionAnswersCreate
  ) {
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/survey_questions/v2/` + caregiverSurveyQuestionId,
      answer
    );
  }

  // PUT /caregivers/:caregiverId/patient_task_instances/:patientTaskInstanceId/update_schedule_date_time
  public updatePatientTaskInstanceScheduleDateTime(
    id: PatientTaskInstanceId,
    params: PatientTaskInstanceEditScheduleDateTime
  ): Promise<messages.EditTaskInstanceScheduleTimeResponse> {
    return this.req<messages.EditTaskInstanceScheduleTimeResponse>(
      "EditTaskInstanceScheduleTimeResponse",
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/patient_task_instances/${id}/update_schedule_date_time`,
      params
    );
  }

  // PUT /caregivers/:caregiverId/patient_task_instances/:patientTaskInstanceId/unschedule_task
  public updatePatientTaskUnschedule(id: PatientTaskInstanceId) {
    return this.req_noResponseBody(
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/patient_task_instances/${id}/unschedule_task`
    );
  }
  // PUT /caregivers/:caregiverId/patient_task_instances/:patientTaskInstanceId/unschedule_task_with_reason
  public updatePatientTaskUnscheduleWithReason(
    id: PatientTaskInstanceId,
    reason: UnscheduleReason
  ) {
    return this.req_noResponseBody(
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/patient_task_instances/${id}/unschedule_task_with_reason`,
      { reason }
    );
  }

  // GET /caregivers/:caregiverId/patient_task_instances/:patientTaskInstanceId/documents/:patientDocumentId
  public getPatientTaskDocument(
    taskInstanceId: PatientTaskInstanceId,
    documentId: PatientDocumentTypeId
  ) {
    return this.req<messages.PatientTaskDocument>(
      "PatientTaskDocument",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/patient_task_instances/${taskInstanceId}/documents/${documentId}`
    );
  }

  // POST /caregivers/:caregiverId/patient_task_instances/:patientTaskInstanceId/patient_documents/:documentTypeId/answer
  public answerPatientTaskInstanceDocumentQuestion(
    taskInstanceId: PatientTaskInstanceId,
    documentId: PatientDocumentTypeId,
    params: PatientDocumentCreateAnswer
  ) {
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/patient_task_instances/${taskInstanceId}/patient_documents/${documentId}/answer`,
      params
    );
  }

  // POST /caregivers/:caregiverId/patient_task_instances/:patientTaskInstanceId/patient_documents/:documentTypeId
  public postSubmitTaskInstancePatientDocument(
    taskInstanceId: PatientTaskInstanceId,
    documentId: PatientDocumentTypeId,
    params: PatientDocumentTaskInstanceSubmitRequest
  ) {
    return this.req<PatientTaskCompletionDate>(
      "PatientTaskCompletionDate",
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/patient_task_instances/${taskInstanceId}/patient_documents/${documentId}`,
      params
    );
  }

  // POST /caregivers/:caregiverId/patient_task_instances/:patientTaskInstanceId/medication_profile
  public postPatientMedicationByTaskInstanceId(
    taskInstanceId: PatientTaskInstanceId,
    params: PatientMedicationProfileCreateParams
  ) {
    return this.req<PatientMedicationProfileItem>(
      "PatientMedicationProfileItem",
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/patient_task_instances/${taskInstanceId}/medication_profile`,
      params
    );
  }

  // PUT /caregivers/:caregiverId/patient_task_instances/:patientTaskInstance/medication_profile
  public updatePatientMedicationByTaskInstanceId(
    taskInstanceId: PatientTaskInstanceId,
    params: PatientMedicationProfileEditParams
  ) {
    return this.req_noResponseBody(
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/patient_task_instances/${taskInstanceId}/medication_profile`,
      params
    );
  }

  // POST /caregivers/:caregiverId/patient_task_instances/:patientTaskInstanceId/patient_document_with_scans
  public uploadScannedDocumentByTaskInstanceId(
    taskInstanceId: PatientTaskInstanceId,
    params: PatientDocumentScanCreate
  ) {
    return this.req<PatientDocumentsPdfUrl>(
      "PatientDocumentsPdfUrl",
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/patient_task_instances/${taskInstanceId}/patient_document_with_scans`,
      params
    );
  }

  // POST /caregivers/:caregiverId/patient_task_instances/:patientTaskInstanceId/patient_contract_diagnosis_codes
  public createPatientDiagnosisCodeByTaskInstanceId(
    taskInstanceId: PatientTaskInstanceId,
    params: CaregiverPatientContractDiagnosisSetParams
  ) {
    return this.req<PatientContractDiagnosisCodes>(
      "PatientContractDiagnosisCodes",
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/patient_task_instances/${taskInstanceId}/patient_contract_diagnosis_codes`,
      params
    );
  }

  // GET /caregivers/:caregiverId/patient_task_instances/:patientTaskInstanceId/patient_diagnosis_codes
  public getPatientDiagnosisCodesByTaskInstanceId(taskInstanceId: PatientTaskInstanceId) {
    return this.req<PatientContractDiagnosisCodes>(
      "PatientContractDiagnosisCodes",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/patient_task_instances/${taskInstanceId}/patient_diagnosis_codes`
    );
  }

  // GET /caregivers/:caregiverId/patient_task_instances/:patientTaskInstanceId/plan_of_care
  public async getTaskPlanOfCare(
    taskInstanceId: PatientTaskInstanceId
  ): Promise<PlanOfCareResponse> {
    return this.req<PlanOfCareResponse>(
      "PlanOfCareResponse",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/patient_task_instances/${taskInstanceId}/plan_of_care`
    );
  }

  // PUT /caregivers/:caregiverId/patient_task_instances/:patientTaskInstanceId/plan_of_care/:planOfCareId/signature
  public async setTaskPlanOfCareSignature(
    planOfCareId: PlanOfCareId,
    taskInstanceId: PatientTaskInstanceId,
    signature: POCSignature
  ): Promise<void> {
    return this.req_noResponseBody(
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/patient_task_instances/${taskInstanceId}/plan_of_care/${planOfCareId}/signature`,
      signature
    );
  }

  // PUT /caregivers/:caregiverId/patient_task_instances/:patientTaskInstanceId/plan_of_care/:planOfCareId/version
  public async setTaskPlanOfCareItemAnswer(
    taskInstanceId: PatientTaskInstanceId,
    planOfCareId: PlanOfCareId,
    answer: PatientDocumentContentItem[]
  ): Promise<void> {
    return this.req_noResponseBody(
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/patient_task_instances/${taskInstanceId}/plan_of_care/${planOfCareId}/version`,
      {
        content: answer,
        isPublished: false,
      }
    );
  }

  // PUT /caregivers/:caregiverId/patient_task_instances/:patientTaskInstanceId/plan_of_care/:planOfCareId/version
  public async submitTaskPlanOfCare(
    taskInstanceId: PatientTaskInstanceId,
    planOfCareId: PlanOfCareId,
    answer: PatientDocumentContentItem[]
  ): Promise<void> {
    return this.req_noResponseBody(
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/patient_task_instances/${taskInstanceId}/plan_of_care/${planOfCareId}/version`,
      {
        content: answer,
        isPublished: true,
      }
    );
  }

  // POST /caregivers/:caregiverId/patient_task_instances/:patientTaskInstanceId/document_type/:docTypeId/document_type_version/:versionId
  public async getDocumentLastAnswersByTaskInstanceId(
    taskInstanceId: PatientTaskInstanceId,
    docId: PatientDocumentTypeId,
    versionId: PatientDocumentVersionId
  ) {
    return this.req<PatientDocumentAnswers>(
      "PatientDocumentAnswers",
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/patient_task_instances/${taskInstanceId}/document_type/${docId}/document_type_version/${versionId}`
    );
  }

  // GET /caregivers/:caregiverId/visit_instances/:visitInstanceId/time_sheet
  public async getTimeSheetByVisitInstanceId(visitInstanceId: VisitInstanceId) {
    return this.req<CaregiverGetManualTimeSheetResponse>(
      "CaregiverGetManualTimeSheetResponse",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/visit_instances/${visitInstanceId}/time_sheet`
    );
  }

  // POST /caregivers/:caregiverId/visit_instance/:visitInstanceId/manual_time_sheet
  public async createTimeSheetByVisitInstanceId(
    visitInstanceId: VisitInstanceId,
    params: CaregiverCreateManualTimeSheetRequest
  ) {
    return this.req<CaregiverCreateManualTimeSheetResponse>(
      "CaregiverCreateManualTimeSheetResponse",
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/visit_instance/${visitInstanceId}/manual_time_sheet`,
      params
    );
  }

  // POST /caregivers/:caregiverId/broadcast_visits/:visitBroadcastId/assign_caregivers_partially/confirm
  public async confirmVisitAssignmentRequest(visitBroadcastId: VisitBroadcastId) {
    const url =
      "/caregivers/:caregiverId/broadcast_visits/:visitBroadcastId/assign_caregivers_partially/confirm"
        .replace(":caregiverId", this.authInfo.caregiverId.toString())
        .replace(":visitBroadcastId", visitBroadcastId.toString());

    return this.req<AssignedWithOvertime>("AssignedWithOvertime", "POST", url);
  }

  // POST caregivers/:caregiverId/broadcast_visits/:visitBroadcastId/assign_caregivers_partially/decline
  public async cancelVisitAssignmentRequest(visitBroadcastId: VisitBroadcastId) {
    const url =
      "/caregivers/:caregiverId/broadcast_visits/:visitBroadcastId/assign_caregivers_partially/decline"
        .replace(":caregiverId", this.authInfo.caregiverId.toString())
        .replace(":visitBroadcastId", visitBroadcastId.toString());

    return this.req_noResponseBody("POST", url);
  }

  // POST /caregivers/:caregiverId/broadcast_visits/:visitBroadcastId/assign_flexible_visit/confirm
  public async confirmFlexibleVisitAssignmentRequest(visitBroadcastId: VisitBroadcastId) {
    const url =
      "/caregivers/:caregiverId/broadcast_visits/:visitBroadcastId/assign_flexible_visit/confirm"
        .replace(":caregiverId", this.authInfo.caregiverId.toString())
        .replace(":visitBroadcastId", visitBroadcastId.toString());

    return this.req<ConfirmedPending>("ConfirmedPending", "POST", url);
  }

  // POST /caregivers/:caregiverId/broadcast_visits/:visitBroadcastId/assign_flexible_visit/decline
  public async cancelFlexibleVisitAssignmentRequest(visitBroadcastId: VisitBroadcastId) {
    const url =
      "/caregivers/:caregiverId/broadcast_visits/:visitBroadcastId/assign_flexible_visit/decline"
        .replace(":caregiverId", this.authInfo.caregiverId.toString())
        .replace(":visitBroadcastId", visitBroadcastId.toString());

    return this.req_noResponseBody("POST", url);
  }

  // GET /caregivers/:caregiverId/visit_instances/:visitInstanceId/duty_sheet
  public async getDutySheetByVisitInstanceId(visitInstanceId: VisitInstanceId) {
    return this.req<VisitInstanceDutySheet>(
      "VisitInstanceDutySheet",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/visit_instances/${visitInstanceId}/duty_sheet`
    );
  }

  // PUT /caregivers/:caregiverId/hr_application_answer
  public async setHRApplicationAnswer(params: HRQuestion) {
    return this.req<HRAnswerResult>(
      "HRAnswerResult",
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/hr_application_answer`,
      params
    );
  }

  // GET /caregivers/:caregiverId/certifications/:certification/application_form
  public async getCaregiverApplication(certification: CaregiverCertification) {
    return this.req<messages.HRApplicationForm>(
      "HRApplicationForm",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/certifications/${certification}/application_form`
    );
  }

  // GET /caregivers/:caregiverId/application_banner
  public async getApplicationBanner() {
    return this.req<messages.CaregiverApplicationBannerResponse>(
      "CaregiverApplicationBannerResponse",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/application_banner`
    );
  }

  // POST /caregivers/:caregiverId/welcome_chat_message
  public sendWelcomeChatMessage() {
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/welcome_chat_message`
    );
  }

  // GET /caregivers/:caregiverId/get_signed_photo_url?url=string
  public async getSignedPhotoUrl(url: string) {
    return this.req<messages.URLResponse>(
      "URLResponse",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/get_signed_photo_url?url=${url}`
    );
  }

  // GET /caregivers/:caregiverId/agencies/:agencyId/compliance_items
  public async getComplianceItems(agencyId: AgencyId) {
    return this.req<messages.CaregiverComplianceItemsMobileResponse>(
      "CaregiverComplianceItemsMobileResponse",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/agencies/${agencyId}/compliance_items`
    );
  }

  // GET /caregivers/:caregiverId/i9_form
  public async getComplianceI9Form() {
    return this.req<messages.CaregiverI9FormResponse>(
      "CaregiverI9FormResponse",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/i9_form`
    );
  }

  // PUT /caregivers/:caregiverId/caregiver_document_upload/:caregiverDocumentUploadId
  public updateCaregiverDocumentUpload(
    caregiverDocumentUploadId: CaregiverDocumentUploadId,
    params: EditCaregiverUploadDocumentMobileRequest
  ) {
    return this.req_noResponseBody(
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/caregiver_document_upload/${caregiverDocumentUploadId}`,
      params
    );
  }

  // GET /caregivers/:caregiverId/comm_center/tickets
  public async getChatTickets() {
    return this.req<messages.CommCenterCaregiverTicketList>(
      "CommCenterCaregiverTicketList",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/comm_center/tickets`
    );
  }

  // GET /caregivers/:caregiverId/comm_center/tickets/:ticketId
  public async getChatTicket(ticketId: CommCenterTicketId) {
    return this.req<messages.CommCenterGetTicketResponse>(
      "CommCenterGetTicketResponse",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/comm_center/tickets/${ticketId}`
    );
  }

  // POST /caregivers/:caregiverId/comm_center/tickets/:commCenterTicketId/message
  public async sendChatMessage(
    ticketId: CommCenterTicketId,
    body: CaregiverSendCommCenterMessageRequest
  ) {
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/comm_center/tickets/${ticketId}/message`,
      body
    );
  }

  // POST /caregivers/:caregiverId/comm_center/tickets/:ticketId/view
  public async markMessageAsRead(
    ticketId: CommCenterTicketId,
    body: CaregiverViewCommCenterTicketMessages
  ) {
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/comm_center/tickets/${ticketId}/view`,
      body
    );
  }

  // POST /caregivers/:caregiverId/comm_center/tickets
  public async createCommCenterTicket(body: CaregiverCreateTicketParams) {
    return this.req<messages.CommCenterGetTicketResponse>(
      "CommCenterGetTicketResponse",
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/comm_center/tickets`,
      body
    );
  }

  // POST /caregivers/:caregiverId/visit_instances/:visitInstanceId/comm_center/tickets
  public async createCommCenterTicketForAssignedVisit(
    body: CommCenterTicketCreateParams.AssigedVisit
  ) {
    return this.req<messages.CommCenterGetTicketResponse>(
      "CommCenterGetTicketResponse",
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/visit_instances/${body.visitInstanceId}/comm_center/tickets`,
      body
    );
  }

  // POST /caregivers/:caregiverId/visit_broadcasts/:visitBrodcastId/comm_center/tickets
  public async createCommCenterTicketForAvailabelVisit(
    body: CommCenterTicketCreateParams.VisitBroadcast
  ) {
    return this.req<messages.CommCenterGetTicketResponse>(
      "CommCenterGetTicketResponse",
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/visit_broadcasts/${body.visitBroadcastId}/comm_center/tickets`,
      body
    );
  }

  // GET /caregivers/:caregiverId/agencies/:agencyId/compliance_form_documents/:documentTypeId
  public async getComplianceFormFocument(
    agencyId: AgencyId,
    documentTypeId: CaregiverDocumentTypeId
  ) {
    return this.req<messages.ComplianceFormDocumentResponse>(
      "ComplianceFormDocumentResponse",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/agencies/${agencyId}/compliance_form_documents/${documentTypeId}`
    );
  }

  // POST /caregivers/:caregiverId/agencies/:agencyId/compliance_form_records
  public async createComplianceFormRecord(
    agencyId: AgencyId,
    body: CreateComplianceFormDocumentRecordRequest
  ) {
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/agencies/${agencyId}/compliance_form_records`,
      body
    );
  }

  // GET /caregivers/:caregiverId/payroll/overview
  public async getPayrollOverview() {
    return this.req<messages.EligibleInstaPayVisitsData>(
      "EligibleInstaPayVisitsData",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/payroll/overview`
    );
  }

  // GET /caregivers/:caregiverId/payroll_information
  public async getPayrollInformation(weekNum: number) {
    return this.req<messages.InstaPayVisitsData>(
      "InstaPayVisitsData",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/payroll_information?weekNum=${weekNum}`
    );
  }

  // GET /caregivers/:caregiverId/insta_pay_info
  public async getInstaPayInfo() {
    return this.req<messages.CaregiverBankInformationWithEmailResponse>(
      "CaregiverBankInformationWithEmailResponse",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/insta_pay_info`
    );
  }

  // POST /caregivers/:caregiverId/withdraw_payment
  public async withdrawPayment(body: WithdrawRequest) {
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/withdraw_payment`,
      body
    );
  }

  // POST /caregivers/:caregiverId/update_bank_information
  public async updateBankInformation(body: CaregiverSetBankInformation) {
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/update_bank_information`,
      body
    );
  }

  // POST /caregivers/:caregiverId/set_bank_information
  public async setBankInformation(body: CaregiverSetBankInformation) {
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/set_bank_information`,
      body
    );
  }

  // GET /caregivers/:caregiverId/insta_pay_info/bank_account_types
  public async getBankAccountTypes() {
    return this.req<BankAccountTypesResponse>(
      "BankAccountTypesResponse",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/insta_pay_info/bank_account_types`
    );
  }

  // GET /caregivers/:caregiverId/insta_pay_info/banks
  public async getBanks() {
    return this.req<BankAccountNamesResponse>(
      "BankAccountNamesResponse",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/insta_pay_info/banks`
    );
  }

  // GET /caregivers/:caregiverId/staffing_preferences
  public async getCaregiverStaffingPreferences() {
    return this.req<CaregiverStaffingPreferencesResponse>(
      "CaregiverStaffingPreferencesResponse",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/staffing_preferences`
    );
  }

  // PUT /caregivers/:caregiverId/staffing_preferences
  public async updateCaregiverStaffingPreferences(
    body: UpdateCaregiverStaffingPreferenceRequest
  ): Promise<void> {
    return this.req_noResponseBody(
      "PUT",
      `/caregivers/${this.authInfo.caregiverId}/staffing_preferences`,
      body
    );
  }

  // POST /caregivers/:caregiverId/notification_status
  public async updatePushNotificationStatus(body: CaregiverUpdatePushNotificationStatusRequest) {
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/notification_status`,
      body
    );
  }

  // GET /caregivers/:caregiverId/paystubs
  public async getCaregiverPaystubs(from?: string, to?: string) {
    const url = `/caregivers/${this.authInfo.caregiverId}/paystubs`;
    const queryParams = from !== undefined || to !== undefined ? "?" : "";
    const fromParam = from !== undefined ? `from=${from}` : "";
    const toParam = to !== undefined ? (from !== undefined ? `&to=${to}` : `to=${to}`) : "";

    return this.req<messages.CaregiverPaystubsResponse>(
      "CaregiverPaystubsResponse",
      "GET",
      url + queryParams + fromParam + toParam
    );
  }

  // POST /caregivers/:caregiverId/paystub
  public async downloadPaystub(body: CaregiverPaystubPdfRequest) {
    return this.req<messages.CaregiverPaystubPdfResponse>(
      "CaregiverPaystubPdfResponse",
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/paystub`,
      body
    );
  }

  // POST /caregivers/:caregiverId/visit_instances/:visitInstanceId/clockin_clockout_record_selfie/:callType
  public async uploadClockInOutVerificationImage(
    visitInstanceId: VisitInstanceId,
    callType: ClockInOutCallType,
    body: ClockinClockoutRecordSelfieCreateParams
  ) {
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/visit_instances/${visitInstanceId}/clockin_clockout_record_selfie/${callType}`,
      body
    );
  }

  // GET /caregivers/:caregiverId/care_moments/progression
  public async getCaregiverRankOverview() {
    return this.req<messages.CaregiverRankOverview>(
      "CaregiverRankOverview",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/care_moments/progression`
    );
  }

  // GET /caregivers/:caregiverId/patient_task_instances/:patientTaskInstanceId/unschedule_reasons
  public async getUnscheduleReasons(patientTaskInstanceId: PatientTaskInstanceId) {
    return this.req<messages.GetTaskUnscheduleReasonsResponse>(
      "GetTaskUnscheduleReasonsResponse",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/patient_task_instances/${patientTaskInstanceId}/unschedule_reasons`
    );
  }

  // POST /caregivers/:caregiverId/care_moments/adjustments/mark_viewed
  public async markCaregiverRankAdjustmentsViewed() {
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/care_moments/adjustments/mark_viewed`
    );
  }

  // GET /caregivers/:caregiverId/raffle
  public async getRaffle() {
    return this.req<messages.GetCaregiverRaffleResponse>(
      "GetCaregiverRaffleResponse",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/raffle`
    );
  }

  // POST /caregivers/:caregiverId/agencies/:agencyId/telephony/calls
  public async createTelephonyCall(
    agencyId: AgencyId,
    body: messages.TelephonyCaregiverCreateOutboundCallRequest
  ) {
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/agencies/${agencyId}/telephony/calls`,
      body
    );
  }

  // GET /caregivers/:caregiverId/agencies/:agencyId/patients/:patientId/taskInstanceId/:taskInstanceId/time_slots
  public async getPatientTimeSlotsForNurse(
    agencyId: AgencyId,
    patientId: PatientId,
    taskInstanceId: PatientTaskInstanceId
  ) {
    return this.req<messages.GetPatientTimeSlotsResponse>(
      "GetPatientTimeSlotsResponse",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/agencies/${agencyId}/patients/${patientId}/taskInstanceId/${taskInstanceId}/time_slots`
    );
  }

  // POST /caregivers/:caregiverId/patient_task_instances/:patientTaskInstanceId/schedule
  public async schedulePatientTaskInstance(
    taskInstanceId: PatientTaskInstanceId,
    body: SchedulePatientTaskInstanceRequest
  ) {
    return this.req_noResponseBody(
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/patient_task_instances/${taskInstanceId}/schedule`,
      body
    );
  }

  // POST /caregivers/:caregiverId/visit_instances/:visitInstanceId/documents/all_submitted
  public checkIfAllDocumentsSubmitted = async (
    visitInstanceId: VisitInstanceId
  ): Promise<boolean> => {
    return this.req<messages.CheckIfAllDocumentsSubmittedResponse>(
      "CheckIfAllDocumentsSubmittedResponse",
      "POST",
      `/caregivers/${this.authInfo.caregiverId}/visit_instances/${visitInstanceId}/documents/all_submitted`
    ).then((response) => response.allSubmitted);
  };

  // GET /caregivers/:caregiverId/patients/:patientId/outsourcing_agency_data
  public getOutsourcingAgencyData = async (
    patientId: PatientId
  ): Promise<messages.OutsourcingAgencyDataResponse> => {
    return this.req<messages.OutsourcingAgencyDataResponse>(
      "OutsourcingAgencyDataResponse",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/patients/${patientId}/outsourcing_agency_data`
    );
  };

  // GET /caregivers/:caregiverId/patients/:patientId/near_future_caregivers
  public getNearFutureCaregivers = async (
    patientId: PatientId
  ): Promise<messages.NearFutureCaregiversResponse> => {
    return this.req<messages.NearFutureCaregiversResponse>(
      "NearFutureCaregiversResponse",
      "GET",
      `/caregivers/${this.authInfo.caregiverId}/patients/${patientId}/near_future_caregivers`
    );
  };

  // GET /caregivers/:caregiverId/patient_task_instances/:patientTaskInstanceId/patient_documents/:documentTypeId/should_ask_for_regenerate_other_documents
  // GET /caregivers/:caregiverId/visit_instances/:visitInstanceId/patient_documents/:documentTypeId/should_ask_for_regenerate_other_documents
  public checkForOtherNursingQuestionsOnDocuments = async (
    params:
      | {
          taskInstanceId: PatientTaskInstanceId;
          documentTypeId: PatientDocumentTypeId;
        }
      | {
          visitInstanceId: VisitInstanceId;
          documentTypeId: PatientDocumentTypeId;
        }
  ): Promise<messages.CheckForOtherNursingQuestionsOnDocumentsResponse> => {
    if ("taskInstanceId" in params) {
      return this.req<messages.CheckForOtherNursingQuestionsOnDocumentsResponse>(
        "CheckForOtherNursingQuestionsOnDocumentsResponse",
        "GET",
        `/caregivers/${this.authInfo.caregiverId}/patient_task_instances/${params.taskInstanceId}/patient_documents/${params.documentTypeId}/should_ask_for_regenerate_other_documents`
      );
    } else {
      return this.req<messages.CheckForOtherNursingQuestionsOnDocumentsResponse>(
        "CheckForOtherNursingQuestionsOnDocumentsResponse",
        "GET",
        `/caregivers/${this.authInfo.caregiverId}/visit_instances/${params.visitInstanceId}/patient_documents/${params.documentTypeId}/should_ask_for_regenerate_other_documents`
      );
    }
  };

  private async req<T>(
    forType: string,
    method: string,
    endpoint: string,
    body?: Object,
    tryRefreshToken = true
  ): Promise<T> {
    await this.refreshTokenIfNeeded();

    const url = `${this.apiConfig.apiBaseUrl}${endpoint}`;
    const req = new HttpRequest(
      method,
      url,
      (body !== undefined ? [API.jsonHeader] : []).concat(
        concatDeviceHeaders([
          [AppVersionHeader, AppVersion],
          [AppPlatformHeader, this.platform],
          this.authHeader(),
        ])
      ),
      body !== undefined ? JSON.stringify(body) : null
    );

    const rsp = await req.fetch();

    if (rsp.status >= 200 && rsp.status < 300) {
      // TODO validate response against JSON schema
      const result = validateApiResponseMessage<T>(forType, rsp.responseText);
      return result;
    } else if (rsp.status === 401) {
      if (tryRefreshToken) {
        await this.refreshToken();
        return this.req(forType, method, endpoint, body, false);
      } else {
        this.authExpired.broadcast(undefined);
        throw new AuthExpiredError();
      }
    } else if (rsp.status === 422) {
      this.appNeedsUpdate.broadcast(undefined);
      throw new AppNeedsUpdateError();
    } else if (rsp.status === 423) {
      this.userIsBlocked.broadcast(undefined);
      throw new AuthUserBlockedError();
    } else if (rsp.status >= 400 && rsp.status < 500) {
      throw new StatusCodeError(rsp.status, url, rsp.responseText);
    } else if (rsp.status >= 500 && rsp.status < 600) {
      throw new ServerError(rsp.status, url);
    } else {
      // TODO Improve this (benny)
      throw new ServerError(rsp.status, url);
    }
  }

  private async validateResponse<T>(params: {
    response: Response;
    url: string;
    forType: string;
    onUnauthorized?: () => Promise<T>;
  }): Promise<T> {
    if (params.response.status >= 200 && params.response.status < 300) {
      return validateApiResponseMessage<T>(
        params.forType,
        JSON.stringify(await params.response.json())
      );
    }

    if (params.response.status === 401) {
      if (params.onUnauthorized !== undefined) {
        return params.onUnauthorized();
      }

      this.authExpired.broadcast(undefined);
      throw new AuthExpiredError();
    }

    if (params.response.status === 422) {
      this.appNeedsUpdate.broadcast(undefined);
      throw new AppNeedsUpdateError();
    }

    if (params.response.status === 423) {
      this.userIsBlocked.broadcast(undefined);
      throw new AuthUserBlockedError();
    }

    if (params.response.status >= 400 && params.response.status < 500) {
      throw new StatusCodeError(params.response.status, params.url, await params.response.json());
    }

    throw new ServerError(params.response.status, params.url);
  }

  private async req_formData<T>(
    params: {
      forType: string;
      method: string;
      endpoint: string;
      body: FormData;
    },
    tryRefreshToken = true
  ): Promise<T> {
    const url = `${this.apiConfig.apiBaseUrl}${params.endpoint}`;

    const response = await fetch(url, {
      method: params.method,
      headers: concatDeviceHeaders([
        [AppVersionHeader, AppVersion],
        [AppPlatformHeader, this.platform],
        this.authHeader(),
      ]),
      body: params.body,
    });

    return this.validateResponse({
      forType: params.forType,
      response: response,
      url: url,
      onUnauthorized: !tryRefreshToken
        ? undefined
        : async () => {
            await this.refreshToken();
            return this.req_formData(params, false);
          },
    });
  }

  private async req_noResponseBody(
    method: string,
    endpoint: string,
    body?: Object,
    tryRefreshToken = true
  ): Promise<void> {
    await this.refreshTokenIfNeeded();

    const url = `${this.apiConfig.apiBaseUrl}${endpoint}`;
    const req = new HttpRequest(
      method,
      url,
      (body !== undefined ? [API.jsonHeader] : []).concat(
        concatDeviceHeaders([
          [AppVersionHeader, AppVersion],
          [AppPlatformHeader, this.platform],
          this.authHeader(),
        ])
      ),
      body !== undefined ? JSON.stringify(body) : null
    );

    const rsp = await req.fetch();

    if (rsp.status >= 200 && rsp.status < 300) {
      // TODO validate that response has no body
      return;
    } else if (rsp.status === 401) {
      if (tryRefreshToken) {
        await this.refreshToken();
        return this.req_noResponseBody(method, endpoint, body, false);
      } else {
        this.authExpired.broadcast(undefined);
        throw new AuthExpiredError();
      }
    } else if (rsp.status === 422) {
      this.appNeedsUpdate.broadcast(undefined);
      throw new AppNeedsUpdateError();
    } else if (rsp.status >= 400 && rsp.status < 500) {
      throw new StatusCodeError(rsp.status, url, rsp.responseText);
    } else if (rsp.status >= 500 && rsp.status < 600) {
      throw new ServerError(rsp.status, url);
    } else {
      // TODO Improve this (benny)
      throw new ServerError(rsp.status, url);
    }
  }
}

/**
 * Signals that the server returned an error
 */
export class StatusCodeError extends Error {
  __proto__: Error; // tslint:disable-line:variable-name
  constructor(statusCode: number, url: string, body: string) {
    const trueProto = new.target.prototype;
    super(`Server returned an error: ${statusCode}, ${url}`);

    this.__proto__ = trueProto;

    this.statusCode = statusCode;
    this.url = url;
    this.body = body;
  }

  readonly statusCode: number;
  readonly url: string;
  readonly body: string;
}

/**
 * Signals that the server returned invalid JSON or data not valid with the expected schema
 */
export class InvalidResponseError extends Error {
  __proto__: Error; // tslint:disable-line:variable-name
  constructor(message: string, detail: string, responseJSON: any, errorJSON: any) {
    const trueProto = new.target.prototype;
    super(
      `Unexpected Server Response: ${message}\n${detail}\n${JSON.stringify(
        errorJSON
      )}\n${JSON.stringify(responseJSON)}`
    );

    this.__proto__ = trueProto;

    this.detail = detail;
    this.responseJSON = responseJSON;
    this.errorJSON = errorJSON;
  }

  readonly detail: string;
  readonly responseJSON: any;
  readonly errorJSON: any;
}

/**
 * Signals that our AuthToken has expired, and we must login again
 */
export class AuthExpiredError extends Error {
  __proto__: Error; // tslint:disable-line:variable-name
  constructor() {
    const trueProto = new.target.prototype;
    super("AuthToken expired or invalid");

    this.__proto__ = trueProto;
  }
}
/**
 * Auth user blocked error
 */
export class AuthUserBlockedError extends Error {
  __proto__: Error; // tslint:disable-line:variable-name
  constructor() {
    const trueProto = new.target.prototype;
    super("User account is blocked");

    this.__proto__ = trueProto;
  }
}

/**
 * Signals that the App version is too old and the user should be instructed to update
 */
export class AppNeedsUpdateError extends Error {
  __proto__: Error; // tslint:disable-line:variable-name
  constructor() {
    const trueProto = new.target.prototype;
    super("You must update the MedFlyt app to the latest version");

    this.__proto__ = trueProto;
  }
}

export function generateRequestId(): string {
  const NUM_BITS = 160;

  const numBytes = Math.floor(NUM_BITS / 8);
  const buf = new Uint8Array(numBytes);
  window.crypto.getRandomValues(buf);
  return Array.prototype.map.call(buf, (x: number) => ("00" + x.toString(16)).slice(-2)).join("");
}

function concatDeviceHeaders(headers: [string, string][]) {
  const deviceUUID = getDeviceUUID();

  if (deviceUUID !== null) {
    headers.push([DeviceUUIDHeader, deviceUUID]);
  }

  return headers;
}
